import { useRef, useEffect, useCallback } from 'react';

interface UseSmoothProgressOptions {
	duration: number;
	isPlaying: boolean;
}

const useSmoothProgress = ({ duration, isPlaying }: UseSmoothProgressOptions) => {
	const progressBarRef = useRef<HTMLDivElement>(null);
	const timeDisplayRef = useRef<HTMLParagraphElement>(null);
	const lastSdkTimeRef = useRef(0);
	const lastSdkTimestampRef = useRef(0);
	const animFrameRef = useRef<number>();
	const currentTimeRef = useRef(0);

	const formatTime = (t: number) => {
		const s = Math.floor(t);
		const m = Math.floor(s / 60);
		return `${m}:${String(s % 60).padStart(2, '0')}`;
	};

	const updateDOM = useCallback((time: number) => {
		currentTimeRef.current = time;
		// Clamp percent to 0-100 range in case duration is stale or smaller than the actual
		// playback time (happens when the Creatomate SDK reports a partial duration right
		// after onReady — otherwise the progress bar overflows past the end of the track).
		const rawPct = duration > 0 ? (time / duration) * 100 : 0;
		const pct = Math.min(100, Math.max(0, rawPct));
		if (progressBarRef.current) {
			progressBarRef.current.style.width = `${pct}%`;
		}
		if (timeDisplayRef.current) {
			const clampedTime = duration > 0 ? Math.min(time, duration) : time;
			timeDisplayRef.current.textContent = `${formatTime(clampedTime)} / ${formatTime(duration)}`;
		}
	}, [duration]);

	const onTimeChange = useCallback((time: number) => {
		lastSdkTimeRef.current = time;
		lastSdkTimestampRef.current = performance.now();
		if (!isPlaying) {
			updateDOM(time);
		}
	}, [isPlaying, updateDOM]);

	useEffect(() => {
		if (isPlaying) {
			const animate = () => {
				const elapsed = (performance.now() - lastSdkTimestampRef.current) / 1000;
				const interpolated = Math.min(lastSdkTimeRef.current + elapsed, duration);
				updateDOM(interpolated);
				animFrameRef.current = requestAnimationFrame(animate);
			};
			animFrameRef.current = requestAnimationFrame(animate);
			return () => {
				if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
			};
		}
	}, [isPlaying, duration, updateDOM]);

	const setTimeImmediate = useCallback((time: number) => {
		lastSdkTimeRef.current = time;
		lastSdkTimestampRef.current = performance.now();
		updateDOM(time);
	}, [updateDOM]);

	return {
		progressBarRef,
		timeDisplayRef,
		onTimeChange,
		setTimeImmediate,
		currentTimeRef,
	};
};

export default useSmoothProgress;
